// The D46 self-assessment disclosure - STRUCTURAL, non-dismissable, on every surface
// that shows a framework readiness result (docs/design/slice4-certify-ux.md §2.4). This
// is the single most load-bearing requirement in the Certify spec: composed from
// `.well` (never `.banner`, whose optional `.x` dismiss control this component must
// never expose - brand.css:215), `role="note"` (a standing disclosure, not a live
// alert - `role="alert"` is reserved for a state CHANGE, ux spec §2.4 a11y note), and a
// real heading so it is never a decorative aside. Copy is DRAFT, flagged for
// counsel/domain-expert review (docs/copy/slice4-certify-copy.md §4) - this component
// renders it verbatim and never authors or edits a word.
import { certifyCopy } from "../copy/certify.js";

export type DisclosureLength = "short" | "medium" | "long";

const KEY_BY_LENGTH = {
  short: "certify.disclosure.short",
  medium: "certify.disclosure.medium",
  long: "certify.disclosure.long",
} as const;

export const SelfAssessmentDisclosure = ({
  length = "medium",
  appName,
  frameworkName,
  headingId,
}: {
  readonly length?: DisclosureLength;
  /** Required for `length="medium"` ({appName}/{frameworkName} substitution); optional
   * otherwise ("short" and "long" do not reference {frameworkName}). */
  readonly appName?: string;
  readonly frameworkName?: string;
  /** Lets a caller (e.g. FrameworkResultCard) wire `aria-labelledby` from its own
   * outer landmark to this disclosure's heading, if it needs to. Optional - a bare
   * render still gets a real, if unlinked, heading. */
  readonly headingId?: string;
}): React.JSX.Element => {
  const text = certifyCopy(KEY_BY_LENGTH[length], {
    appName: appName ?? "",
    frameworkName: frameworkName ?? "",
  });

  return (
    <div className="well pad disclosure" role="note" data-testid="self-assessment-disclosure">
      <div className="row aic gap8">
        <span className="disclosure-glyph" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2 4 6v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V6l-8-4Z" />
          </svg>
        </span>
        <h3 className="disclosure-heading" id={headingId}>
          Self-assessment, not a certification
        </h3>
      </div>
      <p className="muted mt8" style={{ fontSize: 12.5 }}>
        {text}
      </p>
    </div>
  );
};

/** The distinct "verified upload vs. certifies" line (ux spec §2.5, copy §4
 * `certify.disclosure.certificateDistinction`) - renders wherever a founder-uploaded
 * certificate is shown as `verified`. Never merged into `SelfAssessmentDisclosure`
 * itself (the two claims - readiness vs. a verified credential - stay visually and
 * structurally distinct, §2.5). */
export const CertificateDistinctionNote = ({
  appName,
  issuer,
}: {
  readonly appName: string;
  readonly issuer: string;
}): React.JSX.Element => (
  <p className="muted certificate-distinction-note" role="note" style={{ fontSize: 12 }}>
    {certifyCopy("certify.disclosure.certificateDistinction", { appName, issuer })}
  </p>
);
